// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNil, isEmpty, isUndefined, omitBy, set } from "lodash";

// ANT COMPONENTS
import { ArrowLeftOutlined, UserOutlined } from "@ant-design/icons";
import {
  AlertBox,
  BasicModal,
  Button,
  Password,
  PhoneInput,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import { Avatar, Checkbox, Col, Empty, Row, Spin } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";

// STYLE
import "./customer-profile-individual-form.scss";
import "./../customer-management.scss";

// IMPORT TYPE
import {
  CustomerFormInputType,
  CustomerType,
  OrgType,
} from "./../../../types/customer-form.types";
import {
  FilePayload,
  FileTypeFilePayload,
  OrgList,
  ShallowClonedTypeFor,
} from "./../../../types/common.types";
import {
  reverseGeocoding,
  validateGeocoding,
} from "Services/geocoding.services";
import Upload from "Components/shared/upload/upload";
import errorHandler from "Utils/error-handler";
import IndividualCustomerFormSchema from "./customer-profile-individual-form.validation";
import { getBase64 } from "Helpers/getbase64";
import useSession from "Hooks/session.hook";
import { toaster } from "Helpers/toaster";
import LoadingContainer from "Components/shared/loading-container";
import classNames from "classnames";
import CustomerProfileAddress from "../customer-profile-shared/customer-profile-address";
import CustomerProfileParcelTypes from "../customer-profile-shared/customer-profile-parcel";

interface CustomerProfileIndividualFormComponentProps {
  onSubmit?: (data) => void;
  onSubmitEdit?: (data) => void;
  onBack?: () => void;
  customerDetails?: Partial<CustomerFormInputType>;
  orgList: OrgList[];
  isLoading?: boolean;
}

interface CustomerFormErrorType
  extends ShallowClonedTypeFor<Partial<CustomerFormInputType>, string | null> {
  general?: string;
}

// =========================================================
//  Form Handlers
// =========================================================
const useFormHandlers = (props) => {
  const {
    input,
    error,
    prevImg,
    initialError,
    avatarImg,
    setAvatarImg,
    setModal,
    setPrevImg,
    setInput,
    setError,
  } = props || {};

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setError({ ...error, acceptTerms: null });
    setInput({ ...input, acceptTerms: e.target.checked });
  };

  const handlePhoneChange = (
    value: { dialCode: string; number: string },
    name: string,
  ) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleImageChange = ({ fileList: fileList }) => {
    setAvatarImg(fileList);
  };

  const handleImageRemove = () => {
    setAvatarImg([]);
    const fileURL = prevImg?.file_url || "";
    if (fileURL) {
      setInput({ ...input, file: prevImg });
    } else {
      setInput({ ...input, file: undefined });
    }
  };

  const handleConfirmImage = () => {
    setModal(false);
    const fileURL = input.file?.file_url || "";
    if (fileURL && input.file) {
      setPrevImg(input.file);
    }
    avatarImg.forEach(async (fileObj: any) => {
      if (fileObj?.fileID == undefined) {
        let base64Data: any = "";
        base64Data = await getBase64(fileObj.originFileObj);
        setInput({
          ...input,
          file: {
            fileName: fileObj.name,
            fileType: FileTypeFilePayload.IMAGE,
            base64String: base64Data,
          },
        });
      }
    });
  };

  const handleCoordinatesChecking = async (address: any) => {
    const responseData = await reverseGeocoding(address);
    if (responseData && !responseData.error) {
      return responseData.coordinates;
    } else {
      setError({ ...error, country: "Address does not exist" });
    }
  };

  const handleValidateAddress = async (address: any) => {
    const responseData = await validateGeocoding(address);
    if (responseData && !responseData.error) {
      if (responseData?.verdict) return true;
      else return false;
    } else {
      return false;
    }
  };

  return {
    handleFieldChange,
    handleCheckboxChange,
    handleImageChange,
    handlePhoneChange,
    handleImageRemove,
    handleConfirmImage,
    handleSelectChange,
    handleCoordinatesChecking,
    handleValidateAddress,
  };
};

const CustomerProfileIndividualFormComponent: React.FC<
  CustomerProfileIndividualFormComponentProps
> = ({
  onSubmit,
  onSubmitEdit,
  onBack,
  customerDetails,
  orgList,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { org } = useSession();

  const initialInput: Partial<CustomerFormInputType> = {
    acceptTerms: false,
    orgType: OrgType.CUSTOMER,
    customerType: CustomerType.INDIVIDUAL,
    mobileNo: {
      dialCode: "60",
      number: "",
    },
    prefix: "IDV",
    allowPickupAll: true,
  };

  const initialError = {};
  const initialPrevImg: FilePayload = {
    fileName: "",
    fileType: FileTypeFilePayload.IMAGE,
    base64String: "",
  };

  const [input, setInput] =
    useState<Partial<CustomerFormInputType>>(initialInput);
  const [error, setError] = useState<CustomerFormErrorType>(initialError);
  const [modal, setModal] = useState<boolean>(false);
  const [avatarImg, setAvatarImg] = useState<any>([]);
  const [prevImg, setPrevImg] = useState<FilePayload>(initialPrevImg);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  useEffect(() => {
    setInput && setInput({ ...input, ...customerDetails });
  }, [customerDetails]);

  const formHandlerProps = {
    input,
    error,
    prevImg,
    initialError,
    avatarImg,
    setAvatarImg,
    setModal,
    setPrevImg,
    setInput,
    setError,
  };

  const {
    handleFieldChange,
    handleCheckboxChange,
    handleImageChange,
    handlePhoneChange,
    handleImageRemove,
    handleConfirmImage,
    handleSelectChange,
    handleCoordinatesChecking,
    handleValidateAddress,
  } = useFormHandlers({ ...formHandlerProps });

  //TODO: FOR REFACTOR - if can handle API call in the customer-create page, please do so. This was done in a crunch. Many apologies

  const miniAddressNormaliser = (data, omitStreet = false) => {
    return omitBy(
      {
        street1: omitStreet ? undefined : data.street1,
        street2: omitStreet ? undefined : data.street2,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        country: data.country,
      },
      isUndefined,
    );
  };

  const handleSubmit = async () => {
    try {
      const value = IndividualCustomerFormSchema.validateSync(
        omitBy(
          {
            ...input,
            customerName: input?.customerName?.trim(),
          },
          isUndefined,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
        },
      );

      if (!isEmpty(value)) {
        setIsLoadingSubmit && setIsLoadingSubmit(true);
        const addressToCheck = miniAddressNormaliser(input, true);
        const addressValidity = await handleValidateAddress(addressToCheck);

        if (addressValidity) {
          const addressToCheck = miniAddressNormaliser(input);
          const coordinatesObtained = await handleCoordinatesChecking(
            addressToCheck,
          );

          const finalValue = { ...value, coordinates: coordinatesObtained };

          if (input._id) {
            if (onSubmitEdit) {
              onSubmitEdit(finalValue);
            }
          } else {
            if (onSubmit) {
              onSubmit(finalValue);
            }
          }
        } else {
          toaster("error", t("error.addressNotFound"));
        }
      } else {
        setError({ ...error, general: t("error.validationFailed") });
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);
    } catch (err) {
      let { path, value, message }: any = errorHandler.validation(err);
      console.log(path, value, message);
      // e.g. contactNumber.number = contactNumber
      if (path.indexOf(".") !== -1) {
        const str = path.split(".");
        path = str[0];
      }

      let errorObj = {};

      // validation message
      switch (value) {
        case "error.pattern":
          errorObj = {
            [path]: t(`error.pattern`, { field: t(`label.${path}`) }),
          };
          break;
        case "error.validationFailed":
          errorObj = {
            [path]: t(`error.required`, { field: t(`label.${path}`) }),
          };
          break;
        default:
          errorObj = {
            [path]: t(`${value}`, { field: t(`label.${path}`) }),
          };
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);

      // general message
      errorObj = {
        ...errorObj,
        general: t("txt.resolveFormError"),
      };

      setError((prev) => ({ ...prev, ...errorObj }));

      if (document.querySelector(`input[name=${path}]`)) {
        (
          document.querySelector(`input[name=${path}]`) as HTMLInputElement
        ).focus();
      } else if (document.querySelector(`div[name=${path}]`)) {
        (
          document.querySelector(`div[name=${path}] input`) as HTMLInputElement
        ).focus();
      }
    }
  };

  const sharedFormProps = {
    input,
    error,
    handleChange: handleFieldChange,
    handleSelectChange: handleSelectChange,
  };

  return (
    <div
      className={classNames(
        "customer-management customer-profile-edit-details-container",
        {
          "edit-form": input._id,
        },
      )}
    >
      <LoadingContainer isLoading={isLoading || isLoadingSubmit}>
        <form>
          <div className="cm-form-section">
            <div className="cm-form-title">
              {input._id && (
                <span
                  className="cursor-pointer back-action"
                  onClick={() => onBack && onBack()}
                >
                  <ArrowLeftOutlined />
                  <span className="ml-5">{t("txt.back")}</span>
                </span>
              )}
              <span className="header-title">
                {t("title.customerDetails").toUpperCase()}
              </span>
            </div>
            <div className="cm-form-img">
              <div onClick={() => setModal(true)} className="pointer">
                <Avatar
                  size={100}
                  src={input.file?.base64String || input.file?.file_url}
                  icon={<UserOutlined style={{ fontSize: 100 }} />}
                />
              </div>
            </div>
            <div className="cm-form-content">
              <Row gutter={[16, 16]}>
                <Col span={24} lg={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.customerName").toUpperCase()}
                    placeholder={t("txt.customerNameExample")}
                    value={input.customerName}
                    name="customerName"
                    onChange={handleFieldChange}
                    isReadonly={input._id ? true : false}
                  />
                  <AlertBox text={error.customerName} />
                </Col>
                <Col span={24} lg={12}>
                  <TextInput
                    isRequired={true}
                    label={t("label.emailAddress").toUpperCase()}
                    placeholder={t("txt.emailAddressExample")}
                    value={input.emailAddress}
                    name="emailAddress"
                    onChange={handleFieldChange}
                  />
                  <AlertBox text={error.emailAddress} />
                </Col>
                <Col span={24} lg={12}>
                  <CustomerProfileAddress {...sharedFormProps} />
                </Col>
                <Col span={24} lg={12}>
                  <Col xs={24} sm={24} className="mb-6">
                    <PhoneInput
                      isRequired={true}
                      isError={!!error.mobileNo}
                      label={t("label.mobileNo")}
                      name="mobileNo"
                      value={input.mobileNo}
                      onChange={(value) => handlePhoneChange(value, "mobileNo")}
                    />
                    <AlertBox text={error.mobileNo} />
                  </Col>
                  <Col xs={24} sm={24} className="mb-6">
                    <SelectInput
                      showSearch
                      options={orgList.map((org) => {
                        return {
                          label: org.orgName || "",
                          value: org.orgID,
                        };
                      })}
                      filterOption={(inputValue, option) =>
                        option.label
                          .toLowerCase()
                          .indexOf(inputValue.toLowerCase()) >= 0
                      }
                      isRequired={true}
                      value={input.parentOrgID}
                      isReadonly={input._id ? true : false}
                      name="parentOrgID"
                      label={t("label.diolkoCompany")}
                      onChange={(value: string | string[]) =>
                        handleSelectChange(value, "parentOrgID")
                      }
                    />
                    <AlertBox text={error.parentOrgID} />
                  </Col>
                  <Col xs={24} sm={24} className="mb-6">
                    <TextInput
                      isRequired={true}
                      value={input.prefix}
                      maxLength={3}
                      isReadonly={true}
                      name="prefix"
                      label={t("label.prefix")}
                    />
                    <AlertBox text={error.prefix} />
                  </Col>
                </Col>
              </Row>
            </div>
          </div>
          <div className="cm-form-section">
            <div className="cm-form-title">
              <span className="header-title">
                {t("title.parcelType").toUpperCase()}
              </span>
            </div>
            <div className="cm-form-content">
              <CustomerProfileParcelTypes {...sharedFormProps} />
            </div>
          </div>
          {!input._id && (
            <div className="cpif-footer">
              <div className="logistics-divider"></div>
              <div className="cpif-f-terms">
                <Checkbox
                  checked={input.acceptTerms}
                  onChange={handleCheckboxChange}
                >
                  {t("txt.tnc")}
                </Checkbox>
                <AlertBox text={error.acceptTerms} />
              </div>
            </div>
          )}
          {error?.general && (
            <div className="u-info u-error">{error.general}</div>
          )}
          <div className="cm-form-btn">
            <Button
              label={input._id ? t("action.save") : t("action.submit")}
              type="primary"
              onClick={() => handleSubmit()}
            />
          </div>
        </form>
      </LoadingContainer>
      <BasicModal
        isOpen={modal}
        isClosable={false}
        onClose={() => {
          setModal(false);
        }}
        isMaskClosable={false}
        title={t("label.uploadProfilePicture")}
        bodyStyle={{ width: "70vw", maxWidth: "500px", margin: "0 auto" }}
      >
        <Upload
          onCancel={() => {
            setModal(false);
          }}
          onConfirm={() => {
            handleConfirmImage();
          }}
          onChange={handleImageChange}
          onRemove={handleImageRemove}
          fileList={avatarImg}
          uploadText={
            <div className="p-10">
              <p className="ant-upload-text">{t("txt.uploadImgFile")}</p>
              <p className="ant-upload-text">{t("txt.uploadFormat")}</p>
            </div>
          }
        />
      </BasicModal>
    </div>
  );
};

export default CustomerProfileIndividualFormComponent;
